/**
 * SituationCaptureWidget
 *
 * TelePortal Setup step that captures legal status, professional context,
 * and housing plan in a single widget with 3 collapsible sections.
 * Payload produces legalStatus, professional, and housing engine variables
 * matching the SetupProfile shape for instantiation.
 */

import { useCallback, useId, useMemo, useState } from 'react';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';
import type { WidgetProps } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { useTeleportalVisaPathways } from '@tonnie/sdk';
import {
  EMPLOYMENT_SITUATIONS,
  HOUSING_PLANS,
  LEGAL_SITUATIONS,
  type EmploymentSituation,
  type HousingPlan,
  type LegalSituation,
} from '@tonnie/foundation';
import { Button, Card, CardContent, cn } from '@tonnie/ui';
import {
  AlertTriangle,
  ArrowRight,
  Award,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronDown,
  FileText,
  Globe,
  GraduationCap,
  Home,
  Monitor,
  MapPin,
  Rocket,
  Scale,
  Search,
  Users,
  XCircle,
  type LucideIcon,
} from '@tonnie/assets';

// ── Types ──

interface SectionOption {
  id: string;
  title: string;
  description?: string;
  icon: LucideIcon;
  value: string;
}

interface SituationCaptureConfig {
  title?: string;
  description?: string;
}

type TranslateFn = ReturnType<typeof useTranslation>['t'];

/**
 * Copy for one vocabulary value. Written as `(t) => t('literal.key')` so every
 * key stays a LITERAL at its call site — checkable against en.json by the
 * key-usage gate — while the record stays keyed by the shared vocabulary.
 */
interface OptionCopy {
  title: (t: TranslateFn) => string;
  description?: (t: TranslateFn) => string;
  icon: LucideIcon;
}

// ── Option definitions ──
//
// Each list is keyed by the SHARED vocabulary in `@tonnie/foundation` (F38):
// the checklist's conditions are validated against the same constants, so an
// option this widget sends is an answer a condition can match, and a value a
// condition names is one this widget can send. The `Record<…>` types make a
// new vocabulary value a compile error here until it has copy.

const LEGAL_COPY: Record<LegalSituation, OptionCopy> = {
  have_visa: {
    title: (t) => t('copilot.situation.haveVisa'),
    description: (t) => t('copilot.situation.haveVisaDesc'),
    icon: CheckCircle2,
  },
  need_visa: {
    title: (t) => t('copilot.situation.needVisa'),
    description: (t) => t('copilot.situation.needVisaDesc'),
    icon: FileText,
  },
  eu_citizen: {
    title: (t) => t('copilot.situation.euCitizen'),
    description: (t) => t('copilot.situation.euCitizenDesc'),
    icon: Globe,
  },
  family_reunion: {
    title: (t) => t('copilot.situation.familyReunion'),
    description: (t) => t('copilot.situation.familyReunionDesc'),
    icon: Users,
  },
  digital_nomad: {
    title: (t) => t('copilot.situation.digitalNomad'),
    description: (t) => t('copilot.situation.digitalNomadDesc'),
    icon: Monitor,
  },
  company_sponsored: {
    title: (t) => t('copilot.situation.companySponsored'),
    description: (t) => t('copilot.situation.companySponsoredDesc'),
    icon: Briefcase,
  },
  student: {
    title: (t) => t('copilot.situation.studentVisa'),
    description: (t) => t('copilot.situation.studentVisaDesc'),
    icon: GraduationCap,
  },
};

const EMPLOYMENT_COPY: Record<EmploymentSituation, OptionCopy> = {
  company_sponsored: { title: (t) => t('copilot.situation.employedSponsored'), icon: Building2 },
  employed_self: { title: (t) => t('copilot.situation.employedSelf'), icon: Briefcase },
  self_employed: { title: (t) => t('copilot.situation.selfEmployed'), icon: Monitor },
  entrepreneur: { title: (t) => t('copilot.situation.entrepreneur'), icon: Rocket },
  student: { title: (t) => t('copilot.situation.student'), icon: GraduationCap },
  retired: { title: (t) => t('copilot.situation.retired'), icon: Home },
  job_seeking: { title: (t) => t('copilot.situation.jobSeeking'), icon: Search },
};

const HOUSING_COPY: Record<HousingPlan, OptionCopy> = {
  have_housing: { title: (t) => t('copilot.situation.haveHousing'), icon: Home },
  need_temp: { title: (t) => t('copilot.situation.needTemp'), icon: Building2 },
  need_find: { title: (t) => t('copilot.situation.needFind'), icon: Search },
  company_provides: { title: (t) => t('copilot.situation.companyProvides'), icon: Briefcase },
};

function useVocabularyOptions<T extends string>(
  values: readonly T[],
  copy: Record<T, OptionCopy>
): SectionOption[] {
  const { t } = useTranslation();
  return useMemo(
    () =>
      values.map((value) => {
        const { title, description, icon } = copy[value];
        return {
          id: value,
          value,
          title: title(t),
          ...(description ? { description: description(t) } : {}),
          icon,
        };
      }),
    [values, copy, t]
  );
}

/** The "not sure yet" pathway answer — sent as NO pathway, never as a code. */
const PATHWAY_NOT_SURE = 'not_sure';

/** D-6 — the regulated-profession answer, sent as a boolean. */
const CREDENTIAL_YES = 'yes';
const CREDENTIAL_NO = 'no';

// ── Component ──

export function SituationCaptureWidget({ config: _cfg, onComplete }: WidgetProps) {
  const { t } = useTranslation();
  const cfg = (_cfg ?? {}) as SituationCaptureConfig;

  const legalOptions = useVocabularyOptions(LEGAL_SITUATIONS, LEGAL_COPY);
  const professionalOptions = useVocabularyOptions(EMPLOYMENT_SITUATIONS, EMPLOYMENT_COPY);
  const housingOptions = useVocabularyOptions(HOUSING_PLANS, HOUSING_COPY);

  // Three required questions and two optional ones — every answer is real
  // user work. Persist so navigating away mid-form doesn't lose selections.
  const [persisted, patchPersisted] = useWidgetState<{
    legalStatus: string | null;
    visaPathway: string | null;
    professional: string | null;
    credentialRecognition: string | null;
    housing: string | null;
  }>('situation_capture', {
    legalStatus: null,
    visaPathway: null,
    professional: null,
    credentialRecognition: null,
    housing: null,
  });
  const { legalStatus, visaPathway, professional, credentialRecognition, housing } = persisted;
  const setLegalStatus = useCallback(
    (v: string | null) => patchPersisted({ legalStatus: v }),
    [patchPersisted]
  );
  const setVisaPathway = useCallback(
    (v: string | null) => patchPersisted({ visaPathway: v }),
    [patchPersisted]
  );

  // Destination visa pathway/category — drives pathway-specific obligations
  // (incl. company-provided documents) in the Brain instantiation engine via
  // `legalStatus.visaPathway`. The list is the
  // brain catalog's for THIS journey's destination (F38): it used to be four
  // hard-coded pathways, so most of the catalog could never be chosen.
  // Optional: "not sure" leaves the pathway unset and the generic obligations
  // still apply.
  const { activeInstance: journey } = useJourneyInstance();
  const {
    data: pathwayCatalog,
    isLoading: pathwaysLoading,
    isError: pathwaysFailed,
  } = useTeleportalVisaPathways(journey?.id ?? '');
  const visaPathwayOptions = useMemo<SectionOption[]>(
    () => [
      ...(pathwayCatalog?.pathways ?? []).map((p) => ({
        id: p.code,
        value: p.code,
        title: t(p.titleKey),
        description: t(p.descriptionKey),
        icon: FileText,
      })),
      {
        id: PATHWAY_NOT_SURE,
        title: t('copilot.situation.pathway.notSure'),
        description: t('copilot.situation.pathway.notSureDesc'),
        icon: Scale,
        value: PATHWAY_NOT_SURE,
      },
    ],
    [pathwayCatalog, t]
  );
  const credentialOptions = useMemo<SectionOption[]>(
    () => [
      {
        id: CREDENTIAL_YES,
        title: t('copilot.situation.credentialYes'),
        icon: Award,
        value: CREDENTIAL_YES,
      },
      {
        id: CREDENTIAL_NO,
        title: t('copilot.situation.credentialNo'),
        icon: XCircle,
        value: CREDENTIAL_NO,
      },
    ],
    [t]
  );
  const setProfessional = useCallback(
    (v: string | null) => patchPersisted({ professional: v }),
    [patchPersisted]
  );
  const setCredentialRecognition = useCallback(
    (v: string | null) => patchPersisted({ credentialRecognition: v }),
    [patchPersisted]
  );
  const setHousing = useCallback(
    (v: string | null) => patchPersisted({ housing: v }),
    [patchPersisted]
  );

  const required = [legalStatus, professional, housing];
  const allAnswered = required.every((answer) => answer !== null);

  const handleConfirm = useCallback(() => {
    if (!allAnswered) return;
    onComplete?.({
      // `visaPathway` is the structured legal category (distinct from the broad
      // `situation`); 'not_sure' is normalized to undefined so the engine treats
      // it as unset rather than a real pathway.
      legalStatus: {
        situation: legalStatus,
        ...(visaPathway && visaPathway !== PATHWAY_NOT_SURE ? { visaPathway } : {}),
      },
      professional: {
        sponsorship_type: professional,
        // D-6 — unanswered stays unset: the credential-recognition task attaches
        // on an explicit yes, and "did not answer" is not "no".
        ...(credentialRecognition !== null
          ? { needs_credential_recognition: credentialRecognition === CREDENTIAL_YES }
          : {}),
      },
      housing: { plan: housing },
    });
  }, [
    allAnswered,
    legalStatus,
    visaPathway,
    professional,
    credentialRecognition,
    housing,
    onComplete,
  ]);

  const title = cfg.title || t('copilot.situation.title');
  const description = cfg.description || t('copilot.situation.description');

  return (
    <div className="w-full space-y-4 py-2" data-testid="widget-situation_capture">
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Scale className="w-5 h-5 text-primary-accessible" />
          {title}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>

      {/* Section A: Legal */}
      <SectionCard
        title={t('copilot.situation.legalTitle')}
        icon={Scale}
        completed={legalStatus !== null}
        defaultOpen
        testId="widget-situation_capture-section-legal"
      >
        <RadioOptions
          options={legalOptions}
          selected={legalStatus}
          onSelect={setLegalStatus}
          testIdPrefix="widget-situation_capture-legal"
        />
      </SectionCard>

      {/* Section A2: Visa pathway (optional) — the legal category that drives
          pathway-specific obligations, incl. the docs the sponsoring company
          must provide. */}
      <SectionCard
        title={t('copilot.situation.pathwayTitle')}
        icon={FileText}
        completed={visaPathway !== null}
        defaultOpen={legalStatus !== null && visaPathway === null}
        testId="widget-situation_capture-section-pathway"
      >
        <div
          data-testid="widget-situation_capture-pathway-list"
          data-state={pathwaysLoading ? 'loading' : pathwaysFailed ? 'error' : 'ready'}
        >
          {pathwaysFailed && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
              <AlertTriangle className="w-3.5 h-3.5" />
              {t('copilot.situation.pathwaysUnavailable')}
            </p>
          )}
          <RadioOptions
            options={visaPathwayOptions}
            selected={visaPathway}
            onSelect={setVisaPathway}
            testIdPrefix="widget-situation_capture-pathway"
          />
        </div>
      </SectionCard>

      {/* Section B: Professional */}
      <SectionCard
        title={t('copilot.situation.professionalTitle')}
        icon={Briefcase}
        completed={professional !== null}
        defaultOpen={legalStatus !== null && professional === null}
        testId="widget-situation_capture-section-professional"
      >
        <RadioOptions
          options={professionalOptions}
          selected={professional}
          onSelect={setProfessional}
          testIdPrefix="widget-situation_capture-professional"
        />
      </SectionCard>

      {/* Section B2 (optional, D-6): a regulated profession needs its
          qualification recognised before the citizen can practise. */}
      <SectionCard
        title={t('copilot.situation.credentialTitle')}
        icon={Award}
        completed={credentialRecognition !== null}
        defaultOpen={professional !== null && credentialRecognition === null}
        testId="widget-situation_capture-section-credential"
      >
        <p className="text-xs text-muted-foreground mb-2">
          {t('copilot.situation.credentialQuestion')}
        </p>
        <RadioOptions
          options={credentialOptions}
          selected={credentialRecognition}
          onSelect={setCredentialRecognition}
          testIdPrefix="widget-situation_capture-credential"
        />
      </SectionCard>

      {/* Section C: Housing */}
      <SectionCard
        title={t('copilot.situation.housingTitle')}
        icon={MapPin}
        completed={housing !== null}
        defaultOpen={professional !== null && housing === null}
        testId="widget-situation_capture-section-housing"
      >
        <RadioOptions
          options={housingOptions}
          selected={housing}
          onSelect={setHousing}
          testIdPrefix="widget-situation_capture-housing"
        />
      </SectionCard>

      {/* Progress + confirm */}
      <div className="flex items-center justify-between pt-1">
        <p className="text-xs text-muted-foreground">
          {required.filter((answer) => answer !== null).length}/{required.length}{' '}
          {t('copilot.situation.answered')}
        </p>
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={!allAnswered}
          className="gap-1.5"
          data-testid="widget-situation_capture-confirm"
        >
          <CheckCircle2 className="w-4 h-4" />
          {t('common.confirm')}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Sub-components ──

function SectionCard({
  title,
  icon: Icon,
  completed,
  defaultOpen,
  children,
  testId,
}: {
  title: string;
  icon: LucideIcon;
  completed: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const regionId = useId();

  return (
    <Card className="border-border/50" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? regionId : undefined}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center',
              completed ? 'bg-primary/10' : 'bg-muted'
            )}
          >
            {completed ? (
              <CheckCircle2 className="w-4 h-4 text-primary-accessible" />
            ) : (
              <Icon className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <ChevronDown
          className={cn('w-4 h-4 text-muted-foreground transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      {open && (
        <CardContent id={regionId} className="pt-0 pb-4 px-4">
          {children}
        </CardContent>
      )}
    </Card>
  );
}

function RadioOptions({
  options,
  selected,
  onSelect,
  testIdPrefix,
}: {
  options: SectionOption[];
  selected: string | null;
  onSelect: (value: string) => void;
  /**
   * Section-scoped testid prefix so option testids stay unique across
   * sections that share option IDs (e.g. `company_sponsored` and
   * `student` appear in both `legalOptions` and `professionalOptions`).
   * Block-06 spec / POM target options as
   * `${testIdPrefix}-option-${opt.id}`.
   */
  testIdPrefix: string;
}) {
  return (
    <div className="space-y-1.5">
      {options.map((opt) => {
        const isSelected = selected === opt.value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt.value)}
            data-testid={`${testIdPrefix}-option-${opt.id}`}
            className={cn(
              'w-full flex items-center gap-3 p-2.5 rounded-lg border transition-all text-left text-sm',
              isSelected
                ? 'border-primary bg-primary/5 shadow-sm'
                : 'border-transparent hover:bg-muted/50'
            )}
          >
            <div
              className={cn(
                'w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/40'
              )}
            >
              {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
            </div>
            <Icon
              className={cn(
                'w-4 h-4 shrink-0',
                isSelected ? 'text-primary-accessible' : 'text-muted-foreground'
              )}
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium">{opt.title}</p>
              {opt.description && (
                <p className="text-xs text-muted-foreground line-clamp-1">{opt.description}</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
